
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

export function HeroSection() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/7351af5bea0636cfd010072b80e39186.jpg"
          alt="제주도 풍경"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
      </div>

      {/* Floating Navigation */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden shadow-lg">
                <img 
                  src="https://static.readdy.ai/image/c1ead532c50398fda057e46e940be938/e04f99b6877b66d4ca7caa0ce01baead.jpeg" 
                  alt="제주블리 로고"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className={`text-xl sm:text-2xl font-bold transition-colors ${scrolled ? 'text-[#2C2C2C]' : 'text-white'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                  제주블리
                </h1>
                <p className={`text-xs sm:text-sm transition-colors ${scrolled ? 'text-gray-600' : 'text-white/80'}`}>
                  Jeju Bully
                </p>
              </div>
            </motion.div>

            <div className="hidden lg:flex items-center gap-8">
              {[
                { label: '소개', id: 'about' },
                { label: '베스트', id: 'best-tours' },
                { label: '서비스', id: 'services' },
                { label: '차량', id: 'vehicles' },
                { label: '포트폴리오', id: 'portfolio' },
                { label: '후기', id: 'testimonials' },
                { label: '예약', id: 'booking' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-base font-medium transition-all hover:scale-105 ${
                    scrolled ? 'text-gray-700 hover:text-[#FF8E53]' : 'text-white hover:text-[#FF8E53]'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* SNS Icons */}
              <div className="hidden lg:flex items-center gap-2">
                <motion.button
                  onClick={() => window.dispatchEvent(new CustomEvent('openKakaoModal'))}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  title="카카오톡 채널"
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                    scrolled
                      ? 'bg-[#FEE500] text-[#3C1E1E] hover:bg-yellow-300'
                      : 'bg-white/20 text-[#FEE500] hover:bg-[#FEE500] hover:text-[#3C1E1E]'
                  }`}
                >
                  <i className="ri-kakao-talk-fill text-lg"></i>
                </motion.button>
                <motion.a
                  href="https://instagram.com/jejuvelycom"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  title="인스타그램"
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    scrolled
                      ? 'bg-gray-100 text-gray-600 hover:bg-[#E4405F] hover:text-white'
                      : 'bg-white/20 text-white hover:bg-[#E4405F]'
                  }`}
                >
                  <i className="ri-instagram-line text-lg"></i>
                </motion.a>
              </div>

              <motion.a
                href="tel:010-5685-7818"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`hidden sm:flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-3 rounded-full font-medium transition-all whitespace-nowrap shadow-lg ${
                  scrolled
                    ? 'bg-[#FF8E53] text-white hover:bg-[#ff7a3d]'
                    : 'bg-white text-[#FF8E53] hover:bg-gray-100'
                }`}
              >
                <i className="ri-phone-line text-lg"></i>
                <span className="hidden md:inline">010-5685-7818</span>
              </motion.a>

              <motion.button
                onClick={() => window.dispatchEvent(new CustomEvent('openKakaoModal'))}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-3 rounded-full font-medium transition-all whitespace-nowrap shadow-lg bg-[#FEE500] text-[#3C1E1E] hover:bg-yellow-300 cursor-pointer"
              >
                <i className="ri-kakao-talk-fill text-lg"></i>
                <span className="hidden sm:inline font-bold">카카오 상담</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-8 py-20 sm:py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md px-6 py-3 rounded-full mb-8 border border-white/30">
            <i className="ri-star-fill text-[#FFD700] text-xl"></i>
            <span className="text-white text-base sm:text-lg font-medium">제주 로컬 전문가와 함께하는 특별한 여행</span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          특별한 제주여행을<br />
          경험하세요
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-xl sm:text-2xl md:text-3xl text-white/90 mb-12 sm:mb-16 max-w-3xl mx-auto leading-relaxed"
        >
          관광지가 아닌, 로컬만 아는<br />
          숨은 명소와 진짜 맛집을 경험하는<br />
          <span className="text-[#FF8E53] font-bold">프리미엄 맞춤 투어</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-16"
        >
          <motion.button
            onClick={() => scrollToSection('booking')}
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(255, 142, 83, 0.4)' }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-[#FF8E53] to-[#ff7a3d] text-white px-10 sm:px-12 py-5 sm:py-6 rounded-full text-lg sm:text-xl font-bold shadow-2xl hover:shadow-[#FF8E53]/50 transition-all flex items-center gap-3 whitespace-nowrap w-full sm:w-auto justify-center"
          >
            <i className="ri-calendar-check-line text-2xl"></i>
            지금 예약하기
          </motion.button>

          <motion.a
            href="tel:010-5685-7818"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white/20 backdrop-blur-md text-white px-10 sm:px-12 py-5 sm:py-6 rounded-full text-lg sm:text-xl font-bold border-2 border-white/30 hover:bg-white/30 transition-all flex items-center gap-3 whitespace-nowrap w-full sm:w-auto justify-center shadow-xl"
          >
            <i className="ri-phone-line text-2xl"></i>
            010-5685-7818
          </motion.a>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          {[
            { icon: 'ri-user-heart-line', title: '1:1 맞춤 설계', desc: '당신만의 특별한 여행' },
            { icon: 'ri-map-pin-user-line', title: '로컬 큐레이션', desc: '진짜 제주를 경험' },
            { icon: 'ri-customer-service-2-line', title: '24시간 케어', desc: '여행 전부터 후까지' }
          ].map((feature, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#FF8E53] to-[#ff7a3d] rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg">
                <i className={`${feature.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-white/70 text-sm">{feature.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => scrollToSection('best-tours')}
          >
            <span className="text-white/70 text-sm">스크롤하여 더보기</span>
            <i className="ri-arrow-down-line text-white text-2xl"></i>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
